import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, Sparkles, ShieldCheck, BadgeCheck,
  Stethoscope, CalendarCheck, CheckCircle2, Inbox, PenLine,
  HeartHandshake, ClipboardCheck,
} from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';
import { organizationSchema, breadcrumbSchema } from '../lib/seoSchemas';
import { DISCIPLINES } from '../data/disciplines';

/* ───────────────────────────────────────────────────────────────────────
   For Clinicians & Providers — reframed to Provider OS (Phase 7):
   accept referrals, sign in-platform, commit first visits, know
   goals-of-care on day one. Marketplace recruiting + take-rate
   marketing removed (those surfaces are archived).
   ─────────────────────────────────────────────────────────────────── */

const HERO_IMG = 'https://images.pexels.com/photos/36764709/pexels-photo-36764709.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=860&w=720';

const LOOP_STEPS = [
  {
    icon: Inbox,
    title: 'Accept referrals',
    desc: 'Hospital referrals arrive pre-screened, matched to your license, service lines, and coverage area — with coverage already checked before they reach you. First to accept wins.',
  },
  {
    icon: PenLine,
    title: 'Sign in-platform',
    desc: 'F2F, 485 plan of care, NOA, and consent are drafted for every accepted placement and signed in one place. No fax loops, no chasing wet ink.',
  },
  {
    icon: CalendarCheck,
    title: 'Commit the first visit',
    desc: 'Lock a visit window the hospital and the family can both see. The commitment is the confirmation — no status calls.',
  },
  {
    icon: HeartHandshake,
    title: 'Know goals of care on day one',
    desc: 'Directive status and the goals-of-care summary travel with the placement — documented by the care team, visible before your clinician walks in.',
  },
];

const TRUST = [
  {
    icon: BadgeCheck,
    title: 'Credentialed before matched',
    desc: 'License, exclusion, and credentialing checks run before anyone is offered a patient — your agency’s name stays protected.',
  },
  {
    icon: ShieldCheck,
    title: 'PHI on covered paths only',
    desc: 'Patient data never touches a model or service without a signed BAA behind it. Every record view is scoped and logged.',
  },
  {
    icon: ClipboardCheck,
    title: 'A full trace on every placement',
    desc: 'Who was offered, who accepted, when coverage cleared, what was signed — auditable end to end.',
  },
];

export default function ForCliniciansPage() {
  return (
    <div className="min-h-screen bg-cn-bg text-cn-text" data-testid="for-clinicians-page">
      <Seo
        title="Provider OS — Accept Referrals, Sign Orders, Commit First Visits | CaraLoom"
        description="CaraLoom is the provider side of the discharge loop: accept hospital referrals in hours, sign F2F/485/NOA/consent in one place, commit first visits, and know goals-of-care on day one. For home health, hospice, and independent clinicians."
        path="/for-clinicians"
        keywords={['home health referral platform', 'hospice referral software', 'accept hospital referrals', 'provider os', 'home health orders signature', 'post-acute provider software']}
        schema={[organizationSchema(), breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'For clinicians & providers', path: '/for-clinicians' }])]}
      />
      <PublicNavbar />

      {/* ============== HERO ============== */}
      <section className="relative overflow-hidden cn-weave-bg border-b border-cn-border">
        <div className="cn-container pt-12 md:pt-20 pb-16 md:pb-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white dark:bg-cn-ink-800 border border-cn-border text-xs font-semibold text-cn-primary dark:text-cn-sage-200 mb-6">
                <Sparkles className="w-3.5 h-3.5" /> The provider side of the discharge loop
              </div>
              <h1 className="cn-display text-[2.6rem] sm:text-5xl lg:text-[4rem] leading-[1.04] text-cn-text"
                  data-testid="providers-hero-headline">
                Accept. Sign. Commit.{' '}
                <span className="italic font-medium text-cn-primary">On one thread.</span>
              </h1>
              <p className="mt-6 text-lg md:text-xl text-cn-text-muted leading-relaxed max-w-xl">
                Hospital referrals come to you pre-screened, with coverage already checked.
                Accept the placement, sign the orders in one place, and commit the first
                visit — the whole placement workflow without the fax machine.
              </p>

              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Link to="/auth?role=agency" className="cn-btn cn-btn-primary text-base" data-testid="cta-join-provider">
                  Join as a Provider <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/how-it-works" className="cn-btn cn-btn-ghost text-base" data-testid="cta-how-it-works">
                  See how it works <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-cn-text-muted">
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-cn-primary" /> Home health · hospice · palliative</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-cn-primary" /> RN · LPN · PT · OT · SLP · MSW</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-cn-primary" /> Referrals accepted in hours</span>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="relative" data-testid="hero-visual">
                <img
                  src={HERO_IMG}
                  alt="Clinician reviewing a referral"
                  className="w-full h-[420px] object-cover rounded-[2rem] shadow-cn-pop"
                />
                <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-t from-cn-primary/40 via-transparent to-transparent" />

                {/* floating referral card */}
                <div className="absolute -left-3 sm:left-4 top-6 w-[240px] rounded-2xl bg-white dark:bg-cn-ink-800 border border-cn-border shadow-cn-floating p-4">
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-cn-sage-100 flex items-center justify-center">
                      <Inbox className="w-4 h-4 text-cn-primary" />
                    </span>
                    <div>
                      <div className="text-xs font-semibold text-cn-text">New referral</div>
                      <div className="text-[11px] text-cn-text-muted">Skilled nursing · routine</div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-cn-primary">
                      <ShieldCheck className="w-3.5 h-3.5" /> Coverage checked
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-cn-primary text-white px-2.5 py-1 text-[11px] font-semibold">
                      <CalendarCheck className="w-3 h-3" /> Accept
                    </span>
                  </div>
                </div>

                {/* floating signature card */}
                <div className="absolute -right-2 sm:right-4 bottom-6 w-[240px] rounded-2xl bg-white dark:bg-cn-ink-800 border border-cn-border shadow-cn-floating p-4">
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-cn-terracotta-50 flex items-center justify-center">
                      <PenLine className="w-4 h-4 text-cn-terracotta-600" />
                    </span>
                    <div>
                      <div className="text-xs font-semibold text-cn-text">Orders ready</div>
                      <div className="text-[11px] text-cn-text-muted">F2F · 485 POC · NOA · consent</div>
                    </div>
                  </div>
                  <div className="mt-3 text-[11px] text-cn-text-muted">
                    Drafted automatically — sign in one place.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== THE LOOP, PROVIDER-SIDE ============== */}
      <section className="py-20 md:py-28" data-testid="provider-loop">
        <div className="cn-container">
          <div className="max-w-3xl">
            <div className="cn-eyebrow mb-3">Your side of the loop</div>
            <h2 className="cn-display text-4xl md:text-5xl">Four moments. Zero fax machines.</h2>
            <p className="mt-4 text-lg text-cn-text-muted">
              CaraLoom optimizes the two moments that decide a placement — accepting the
              offer and signing the orders — and keeps everything else out of your way.
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {LOOP_STEPS.map((p) => (
              <div key={p.title} className="cn-card cn-card-hover p-8" data-testid={`loop-step-${p.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <span className="w-12 h-12 rounded-2xl bg-cn-sage-100 flex items-center justify-center">
                  <p.icon className="w-6 h-6 text-cn-primary" />
                </span>
                <h3 className="mt-5 font-display font-semibold text-xl text-cn-text">{p.title}</h3>
                <p className="mt-2.5 text-cn-text-muted leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== TRUST ============== */}
      <section className="py-20 md:py-28 bg-cn-bone-100 dark:bg-cn-ink-900 border-y border-cn-border" data-testid="provider-trust">
        <div className="cn-container">
          <div className="max-w-3xl">
            <div className="cn-eyebrow mb-3">Built for the compliance review</div>
            <h2 className="cn-display text-4xl md:text-5xl">Your license, protected by default.</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {TRUST.map((t) => (
              <div key={t.title} className="cn-card p-8 bg-white dark:bg-cn-ink-800" data-testid={`provider-trust-${t.title.toLowerCase().replace(/\s+/g, '-').slice(0, 24)}`}>
                <span className="w-12 h-12 rounded-2xl bg-cn-sage-100 flex items-center justify-center">
                  <t.icon className="w-6 h-6 text-cn-primary" />
                </span>
                <h3 className="mt-5 font-display font-semibold text-xl text-cn-text">{t.title}</h3>
                <p className="mt-2.5 text-cn-text-muted leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== FINAL CTA ============== */}
      <section className="py-20 md:py-24">
        <div className="cn-container">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-cn-primary text-white p-10 md:p-16">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/5" />
            <div className="relative max-w-2xl">
              <Stethoscope className="w-8 h-8 text-cn-terracotta-300" />
              <h2 className="cn-display text-4xl md:text-5xl mt-5 !text-white">Ready to take the next referral?</h2>
              <p className="mt-4 text-lg text-white/80">
                Onboard your organization, verify your clinicians, and start accepting
                pre-screened hospital referrals — with the paperwork already drafted.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/auth?role=agency" className="cn-btn bg-white text-cn-primary hover:bg-cn-bone-100 text-base" data-testid="final-cta-join-provider">
                  Join as a Provider <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/contact" className="cn-btn bg-white/10 text-white border border-white/20 hover:bg-white/15 text-base" data-testid="final-cta-contact">
                  Talk to us <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Discipline-specific pages (reference — retired from the index) */}
      <section className="py-16 md:py-20 bg-cn-bone-100 dark:bg-cn-ink-900 border-t border-cn-border" data-testid="for-clinicians-disciplines">
        <div className="cn-container">
          <div className="cn-eyebrow mb-3">By discipline</div>
          <h2 className="cn-display text-3xl sm:text-4xl mb-3 max-w-2xl">How the loop works for your license.</h2>
          <p className="text-cn-text-muted max-w-2xl mb-8 text-sm sm:text-base">
            Every discipline joins the discharge loop a little differently — what you sign,
            what you commit to, and what lands in your work queue. Pick yours.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {DISCIPLINES.map((d) => (
              <Link
                key={d.slug}
                to={`/for-${d.slug}`}
                className="cn-card p-5 hover:border-cn-primary/50 hover:shadow-md transition-all group bg-white dark:bg-cn-ink-800"
                data-testid={`discipline-card-${d.slug}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-display font-bold text-cn-text dark:text-white">{d.name}</h3>
                    <p className="text-[11px] uppercase tracking-wider text-cn-text-muted font-bold mt-1">{d.setting}</p>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-cn-text-muted group-hover:text-cn-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all flex-shrink-0" />
                </div>
                <p className="mt-3 text-sm text-cn-text-muted line-clamp-2">{d.headline}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
